// app/composables/supabase/useAuthBroadcast.ts
// Cross-tab signal for Supabase auth success. The magic-link email opens /confirm in a NEW tab,
// where the PKCE exchange + session cookie are set; the ORIGINAL tab (which still holds the
// in-progress prediction) must learn about it to continue. We coordinate via BroadcastChannel,
// with a localStorage fallback for browsers without it (e.g. some Safari versions).

const CHANNEL = "sb-auth";
const MESSAGE = "authenticated";
const STORAGE_KEY = "sb-auth-success";

interface UseAuthBroadcast {
  notifyAuthenticated: () => void;
  onAuthenticated: (cb: () => void) => () => void;
}

export const useAuthBroadcast = (): UseAuthBroadcast => {
  // Announce (from the /confirm tab) that login succeeded.
  function notifyAuthenticated() {
    if (typeof window === "undefined") return;
    try {
      if ("BroadcastChannel" in window) {
        const ch = new BroadcastChannel(CHANNEL);
        ch.postMessage(MESSAGE);
        ch.close();
      }
    } catch (e) {
      console.error("useAuthBroadcast notify (BroadcastChannel):", e);
    }
    // Fallback: a value change fires the `storage` event in other tabs of the same origin.
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {
      console.error("useAuthBroadcast notify (localStorage):", e);
    }
  }

  // Listen (in the original tab) for the auth-success signal. Returns a cleanup function.
  function onAuthenticated(cb: () => void): () => void {
    if (typeof window === "undefined") return () => {};

    let channel: BroadcastChannel | null = null;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) cb();
    };

    try {
      if ("BroadcastChannel" in window) {
        channel = new BroadcastChannel(CHANNEL);
        channel.onmessage = event => {
          if (event.data === MESSAGE) cb();
        };
      }
    } catch (e) {
      console.error("useAuthBroadcast listen (BroadcastChannel):", e);
    }
    window.addEventListener("storage", onStorage);

    return () => {
      try {
        channel?.close();
      } catch {
        /* noop */
      }
      window.removeEventListener("storage", onStorage);
    };
  }

  return { notifyAuthenticated, onAuthenticated };
};
